import { cloneDeep } from 'lodash-es';

export type TApplovinRegionMode = 'global' | 'us';

const DEFAULT_ALLOWED_OS = ['1', '2', '255'];
const DEFAULT_ALL_MARKETS = '255';
const DEFAULT_US_MARKET = 'us';
const DEFAULT_ALLOWED_MARKETS = [DEFAULT_ALL_MARKETS, DEFAULT_US_MARKET];
const DEFAULT_APPL_CODE_SUBSTRING = 'applovin';

export interface IApplovinRegionCodesConfig {
  /**
   * 未配置 allowedUaNetworkCodes / applovinChannelCodes 时，从 selector（与 applovinCustomFilterKey 一致，默认 ua_network）筛选项中取
   * `code` 包含该子串的项（不区分大小写），默认 `applovin`
   */
  applovinCodeMatchSubstring?: string;
  /** 允许的 platform/os，与筛选器 system 一致；可含 `255` 表示全选 */
  allowedOsCodes?: string[];
  /**
   * 允许的 market / 国家 code（与 allowedOsCodes 同为列表）。
   * 未使用 `applovinCodesByRegionMode` 时：需同时含 Global 占位 `255` 与 US 用 `us`，用于校验与拼请求。
   * 使用 `applovinCodesByRegionMode` 时：建议在 `us` 中配置；`global` 可不配（不做国家白名单交集校验）。
   */
  allowedMarketsListCode?: string[];
  /**
   * 合并进请求的 custom_filters：若接口已有同 key 则保留接口值；
   * 仅补全缺失项。默认 version/user_type/campaign/network_type → Global 全量占位（见 getGlobalMarketListCode）
   */
  extraCustomFilterDefaults?: Array<{
    key: string;
    filter_list: string[];
  }>;
}

/** 按 Global / US Tab 分层覆盖 `IApplovinRegionCodesConfig`；与扁平字段合并顺序见 mergeApplovinRegionCodesForMode */
export interface IApplovinRegionCodesByMode {
  global?: Partial<IApplovinRegionCodesConfig>;
  us?: Partial<IApplovinRegionCodesConfig>;
}

/**
 * 公共层（扁平 + applovinCodesConfig）先合并为 base，再按 Tab 叠加：
 * - global：`base` → `global` 覆盖
 * - us：`base` → `global` 覆盖 → `us` 覆盖（`us: {}` 表示不在本层改任何字段）
 */
export function mergeApplovinRegionCodesForMode(
  base: IApplovinRegionCodesConfig | undefined,
  byMode: IApplovinRegionCodesByMode | undefined,
  regionMode: TApplovinRegionMode,
): IApplovinRegionCodesConfig | undefined {
  if (!byMode) {
    return base;
  }
  const hasGlobal = byMode.global && Object.keys(byMode.global).length > 0;
  const hasUs = byMode.us && Object.keys(byMode.us).length > 0;
  if (!hasGlobal && !hasUs) {
    return base;
  }
  if (regionMode === 'global') {
    return mergeApplovinRegionCodesConfigLayers(base, byMode.global);
  }
  return mergeApplovinRegionCodesConfigLayers(base, byMode.global, byMode.us);
}

const APPL_REGION_CODES_FLAT_KEYS: (keyof IApplovinRegionCodesConfig)[] = [
  'allowedOsCodes',
  'allowedMarketsListCode',
  'applovinCodeMatchSubstring',
  'extraCustomFilterDefaults',
];

/**
 * 从 pageConfig 节点上读取与 OS/国家相关的扁平字段（可与 `applovinCodesConfig` 嵌套合并）
 */
export function pickApplovinRegionCodesFlat(
  src: Record<string, unknown> | undefined | null,
): Partial<IApplovinRegionCodesConfig> {
  if (!src || typeof src !== 'object') {
    return {};
  }
  const out: Partial<IApplovinRegionCodesConfig> = {};
  APPL_REGION_CODES_FLAT_KEYS.forEach(k => {
    const v = src[k as string];
    if (v !== undefined) {
      (out as Record<string, unknown>)[k as string] = v;
    }
  });
  return out;
}

/**
 * 多层合并，后者覆盖前者；值为 `undefined` 的项不覆盖。
 */
export function mergeApplovinRegionCodesConfigLayers(
  ...layers: Array<Partial<IApplovinRegionCodesConfig> | undefined>
): IApplovinRegionCodesConfig | undefined {
  const acc: Partial<IApplovinRegionCodesConfig> = {};
  layers.forEach(layer => {
    if (!layer) {
      return;
    }
    (Object.keys(layer) as (keyof IApplovinRegionCodesConfig)[]).forEach(k => {
      const v = layer[k];
      if (v !== undefined) {
        (acc as Record<string, unknown>)[k as string] = v;
      }
    });
  });
  if (Object.keys(acc).length > 0) {
    return acc as IApplovinRegionCodesConfig;
  }
  return undefined;
}

export type TNormalizedApplovinCodes = {
  applovinCodeMatchSubstring: string;
  allowedOsCodes: string[];
  allowedMarketsListCode: string[];
  extraCustomFilterDefaults: Array<{
    key: string;
    filter_list: string[];
  }>;
};

type TLegacyCodes = IApplovinRegionCodesConfig & {
  allMarketsListCode?: string;
  usMarketListCode?: string;
};

function resolveAllowedMarketsListCode(codes?: TLegacyCodes): string[] {
  if (codes?.allowedMarketsListCode?.length) {
    return [...codes.allowedMarketsListCode];
  }
  if (
    codes?.allMarketsListCode !== undefined ||
    codes?.usMarketListCode !== undefined
  ) {
    return [
      codes.allMarketsListCode ?? DEFAULT_ALL_MARKETS,
      codes.usMarketListCode ?? DEFAULT_US_MARKET,
    ];
  }
  return [...DEFAULT_ALLOWED_MARKETS];
}

/** Global / zone / channel / lang 等「全市场」占位：优先取列表中的 `255`，否则取第一项 */
export function getGlobalMarketListCode(
  codes: Pick<TNormalizedApplovinCodes, 'allowedMarketsListCode'>,
): string {
  const arr = codes.allowedMarketsListCode;
  const hit = arr.find(x => x === DEFAULT_ALL_MARKETS);
  return hit ?? arr[0] ?? DEFAULT_ALL_MARKETS;
}

/** US Tab 的 `market_list`：取列表中 code 为 `us`（忽略大小写）的项，缺省为 `us` */
export function getUsMarketListCode(
  codes: Pick<TNormalizedApplovinCodes, 'allowedMarketsListCode'>,
): string {
  const arr = codes.allowedMarketsListCode;
  const hit = arr.find(x => x.toLowerCase() === DEFAULT_US_MARKET);
  return hit ?? DEFAULT_US_MARKET;
}

export function normalizeCodesConfig(
  codes?: TLegacyCodes,
): TNormalizedApplovinCodes {
  const allowedMarketsListCode = resolveAllowedMarketsListCode(codes);
  const ph = getGlobalMarketListCode({ allowedMarketsListCode });

  let extra: Array<{ key: string; filter_list: string[]; }>;
  if (codes?.extraCustomFilterDefaults?.length) {
    extra = codes.extraCustomFilterDefaults.map(d => ({
      key: d.key,
      filter_list: [...d.filter_list],
    }));
  } else {
    extra = [
      { key: 'version', filter_list: [ph] },
      { key: 'user_type', filter_list: [ph] },
      { key: 'campaign', filter_list: [ph] },
      { key: 'network_type', filter_list: [ph] },
    ];
  }

  let allowedOsCodes: string[];
  if (codes?.allowedOsCodes?.length) {
    allowedOsCodes = [...codes.allowedOsCodes];
  } else {
    allowedOsCodes = [...DEFAULT_ALLOWED_OS];
  }

  return {
    applovinCodeMatchSubstring:
      codes?.applovinCodeMatchSubstring ?? DEFAULT_APPL_CODE_SUBSTRING,
    allowedOsCodes,
    allowedMarketsListCode,
    extraCustomFilterDefaults: extra,
  };
}

/** 级联或普通自定义筛选项取值 */
export const getCascadeChildValues = (
  filterVal: Record<string, unknown> | undefined,
  key: string,
): string[] => {
  if (!filterVal) {
    return [];
  }
  const cv = filterVal[key];
  if (cv === undefined || cv === null) {
    return [];
  }
  if (
    typeof cv === 'object' &&
    !Array.isArray(cv) &&
    (cv as { type?: string; }).type
  ) {
    const o = cv as Record<string, unknown>;
    const child = o[key];
    if (Array.isArray(child)) {
      return child.map(String);
    }
    if (child !== undefined && child !== null) {
      return [String(child)];
    }
    return [];
  }
  if (Array.isArray(cv)) {
    return cv.map(String);
  }
  return [String(cv)];
};

type TSelectorItemLite = {
  code?: string;
};

/**
 * 未配置 ua_network 具体 code 列表时：从 selector 对应项中取 `code` 包含指定子串（默认 applovin，忽略大小写）
 */
export const resolveApplovinCodesFromSelectorItems = (
  items: TSelectorItemLite[] | null | undefined,
  matchSubstring?: string,
): string[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  const sub = (matchSubstring ?? DEFAULT_APPL_CODE_SUBSTRING).toLowerCase();
  return items
    .filter(it => String(it?.code ?? '').toLowerCase().includes(sub))
    .map(it => String(it?.code || '').trim())
    .filter(Boolean);
};

/**
 * 筛选器 OS 与 allowedOsCodes 取交集作为 os_list。
 * - 选 255 或未选 platform：展开为白名单中所有非 255 的值。
 * - 有选：交集为空则返回 null（不发请求，由上层 beforeRequest 返回 false → 无数据）。
 */
export const resolveOsListForRequest = (
  filterVal: Record<string, unknown> | undefined,
  codesConfig: TNormalizedApplovinCodes,
): string[] | null => {
  const allowed = new Set(codesConfig.allowedOsCodes.map(String));
  const nonAllCodes = codesConfig.allowedOsCodes
    .map(String)
    .filter(c => c !== DEFAULT_ALL_MARKETS);
  const sys = filterVal?.system;
  let raw: string[];
  if (Array.isArray(sys)) {
    raw = sys.map(String);
  } else if (sys !== undefined && sys !== null) {
    raw = [String(sys)];
  } else {
    raw = [];
  }
  if (
    raw.length === 0 ||
    raw.includes(DEFAULT_ALL_MARKETS)
  ) {
    return nonAllCodes.length > 0 ? nonAllCodes : null;
  }
  const intersection = raw.map(String).filter(o => allowed.has(o));
  return intersection.length > 0 ? intersection : null;
};

/** 读取筛选器 region.markets；无有效选择时返回 null */
export const resolveRegionMarketsFromFilter = (
  filterVal: Record<string, unknown> | undefined,
): string[] | null => {
  const markets = filterVal?.region as
    | { markets?: string[]; }
    | undefined;
  const m = markets?.markets;
  if (!m || !Array.isArray(m) || m.length === 0) {
    return null;
  }
  const trimmed = m.map(x => String(x).trim()).filter(Boolean);
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Global Tab：`market_list` 与筛选器国家一致。
 * - 未选 / 选含 `255` → 全量占位 `[getGlobalMarketListCode]`
 * - 仅选具体国家 → 去重后的 code 列表
 */
export const resolveGlobalMarketListFromFilter = (
  filterVal: Record<string, unknown> | undefined,
  codesConfig: TNormalizedApplovinCodes,
): string[] => {
  const raw = resolveRegionMarketsFromFilter(filterVal);
  if (!raw || raw.some(x => String(x).trim() === DEFAULT_ALL_MARKETS)) {
    return [getGlobalMarketListCode(codesConfig)];
  }
  return [...new Set(raw.map(x => String(x).trim()))];
};

/**
 * region.markets 与 allowedMarketsListCode 取交集；有选国家且交集为空 → false。
 * 选 255 或未选国家 → true（255 等价于全选，直接放行）。
 * 仅用于 **US Tab**。
 */
export const validateUsMarketSelection = (
  filterVal: Record<string, unknown> | undefined,
  codesConfig: TNormalizedApplovinCodes,
): boolean => {
  const markets = filterVal?.region as { markets?: string[]; } | undefined;
  const m = markets?.markets;
  if (!m || !Array.isArray(m) || m.length === 0) {
    return true;
  }
  if (m.some(x => String(x).trim() === DEFAULT_ALL_MARKETS)) {
    return true;
  }
  const allowed = new Set(codesConfig.allowedMarketsListCode.map(String));
  const intersection = m
    .map(x => String(x).trim())
    .filter(x => allowed.has(x));
  return intersection.length > 0;
};

/**
 * ua_network 与 allowedUaNetworkCodes（解析后的 applovinCodes）取交集作为 filter_list。
 * 选 255 或未勾选时展开为白名单中所有非 255 的值；部分勾选时仅发交集；交集为空返回 null。
 */
export const resolveApplovinFilterList = (
  filterVal: Record<string, unknown> | undefined,
  filterKey: string,
  applovinCodes: string[],
  codesConfig: TNormalizedApplovinCodes,
): string[] | null => {
  if (!applovinCodes.length) {
    return null;
  }
  const allToken = getGlobalMarketListCode(codesConfig);
  const selected = getCascadeChildValues(filterVal, filterKey);
  const nonAll = selected.filter(s => s !== allToken);
  const nonAllCodes = applovinCodes.filter(c => c !== allToken);

  if (nonAll.length === 0) {
    return nonAllCodes.length > 0 ? nonAllCodes : null;
  }
  const allowedSet = new Set(applovinCodes.map(String));
  const intersection = nonAll.filter(c => allowedSet.has(c));
  return intersection.length > 0 ? intersection : null;
};

type TCustomFilterRow = {
  key: string;
  filter_list: string[];
};

export const mergeApplovinRegionIntoTrendPayload = (
  base: Record<string, unknown>,
  ctx: {
    regionMode: TApplovinRegionMode;
    /** Global Tab 下用于拼 `market_list`（不按国家聚合，见 accumulation.by_country） */
    filterVal?: Record<string, unknown>;
    applovinCustomFilterKey: string;
    applovinFilterList: string[];
    osList: string[];
    codesConfig: TNormalizedApplovinCodes;
  },
): Record<string, unknown> => {
  const cfg = ctx.codesConfig;
  const next = cloneDeep(base);
  let filters: Record<string, unknown>;
  if (typeof next.filters === 'object' && next.filters !== null) {
    filters = { ...(next.filters as Record<string, unknown>) };
  } else {
    filters = {};
  }

  const ph = getGlobalMarketListCode(cfg);
  filters.zone_list = [ph];
  filters.channel_list = [ph];
  filters.lang_list = Array.isArray(filters.lang_list) ? filters.lang_list : [ph];

  if (ctx.regionMode === 'global') {
    filters.market_list = resolveGlobalMarketListFromFilter(
      ctx.filterVal,
      cfg,
    );
  } else {
    const nonAllMarkets = cfg.allowedMarketsListCode.filter(
      c => c !== DEFAULT_ALL_MARKETS,
    );
    filters.market_list =
      nonAllMarkets.length > 0
        ? nonAllMarkets
        : [getUsMarketListCode(cfg)];
  }

  filters.os_list = ctx.osList;

  const existing = Array.isArray(filters.custom_filters)
    ? (filters.custom_filters as TCustomFilterRow[])
    : [];
  const byKey = new Map<string, string[]>();
  existing.forEach(c => {
    if (c?.key) {
      byKey.set(c.key, Array.isArray(c.filter_list) ? c.filter_list : [ph]);
    }
  });

  byKey.set(ctx.applovinCustomFilterKey, ctx.applovinFilterList);

  cfg.extraCustomFilterDefaults.forEach(({ key, filter_list }) => {
    if (!byKey.has(key)) {
      byKey.set(key, [...filter_list]);
    }
  });

  filters.custom_filters = Array.from(byKey.entries()).map(
    ([key, filter_list]) => ({ key, filter_list }),
  );

  next.filters = filters;

  const acc =
    next.accumulation && typeof next.accumulation === 'object'
      ? (next.accumulation as Record<string, unknown>)
      : {};
  /** Global Tab 始终不按国家聚合；仅 US Tab 传 by_country: true */
  next.accumulation = {
    ...acc,
    by_country: ctx.regionMode === 'us',
    by_system: true,
  };

  return next;
};

/**
 * beforeRequest 在「交集为空」等场景返回 `{ [_key]: true }`，不发真实 HTTP；
 * 由 KpiApplovinRegionTrendService 注入的包装 api 识别后 resolve 空 metrics_list，走 Service 正常 then → 展示无数据。
 */
export const APPLOVIN_SKIP_REQUEST_KEY = '_applovinSkipRequest';

export function createApplovinEmptyOverallPerformanceResponse(): {
  code: number;
  data: { metrics_list: unknown[]; };
} {
  return {
    code: 0,
    data: {
      metrics_list: [],
    },
  };
}
